import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {AnalyticsPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroup} from '../../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroup';
import {ButtonGroupSpacer} from '../../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroupSpacer';
import {useClipboard} from '../../../../../libs/shared/ui/src/hooks/clipboardHookHelpers';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import EmptyStateErrorImgSrc from '../../../../assets/images/error2.png';
import {useAppDispatch, useAppSelector} from '../../../../helpers/appReduxHelpers';
import {AppState} from '../../../../store/appState';
import {buildAnalyticsAutomationQueryFromState} from '../../../../store/panels/analytics/analyticsPanelHelpers';
import {openHelpChatAction} from '../../../../store/panels/app/appPanelActions';

/*
 * Props.
 */

interface KnowledgeBaseReportErrorBoundaryProps {
  panelId: AnalyticsPanelId;
  error: any;
}

/*
 * Style.
 */

const StyledErrorWrapperDiv = styled.div`
  display: grid;
  align-items: center;
  align-content: center;
  background: ${(p) => p.theme.greys.shade20};
  margin: -12px -48px 0 -48px;
`;

const StyledImg = styled.img`
  margin: auto;
  margin-bottom: 24px;
  width: 92px;
  height: 67px;
`;

const StyledTitleDiv = styled.div`
  font-size: ${fontSizes.veryLarge};
  font-weight: ${fontWeights.semibold};
  color: ${(p) => p.theme.greys.shade80};
  line-height: 28px;
  max-width: 310px;
  margin: auto;
  text-align: center;
  margin-bottom: 8px;
`;

const StyledDescriptionDiv = styled.div`
  color: ${(p) => p.theme.greys.shade70};
  max-width: 310px;
  line-height: 21px;
  margin: auto;
  text-align: center;
  margin-bottom: 24px;
`;

/*
 * Component.
 */

export const KnowledgeBaseReportErrorBoundary: FC<KnowledgeBaseReportErrorBoundaryProps> = ({
  panelId,
  error,
}) => {
  const {copy} = useClipboard();
  const filters = useAppSelector((state: AppState) => buildAnalyticsAutomationQueryFromState(state));
  const dispatch = useAppDispatch();

  const onContactUsClick = () => {
    dispatch(openHelpChatAction(panelId));
  };

  return (
    <StyledErrorWrapperDiv>
      <StyledImg src={EmptyStateErrorImgSrc} />
      <StyledTitleDiv>
        <Trans id="analytics-knowledge-base-error-title">Oops, something went wrong!</Trans>
      </StyledTitleDiv>
      <StyledDescriptionDiv>
        <Trans id="analytics-knowledge-base-error-description">
          Our systems are running into an issue right now. Try reloading and if this persists, contact us with
          a copy-paste of the error details.
        </Trans>
      </StyledDescriptionDiv>
      <ButtonGroup>
        <ButtonGroupSpacer />
        <Button
          variant={ButtonVariantsEnum.SECONDARY}
          onClick={() =>
            copy(
              JSON.stringify({
                filters,
                message: error.message,
                stack: error.stack,
              }),
            )
          }
          leftIcon="copySmall"
        >
          <Trans id="analytics-knowledge-base-error-copy-error-details">Copy error details</Trans>
        </Button>
        <Button variant={ButtonVariantsEnum.PRIMARY} onClick={onContactUsClick}>
          <Trans id="analytics-knowledge-base-error-contact-us">Contact us</Trans>
        </Button>
        <ButtonGroupSpacer />
      </ButtonGroup>
    </StyledErrorWrapperDiv>
  );
};
